/**
 * ExplorePanel — the one card on the home screen. Replaces the Worlds/Settings buttons and the
 * dark popover they used to open: everything is on screen at once, in a fixed vertical order, so
 * there is no menu to navigate.
 *
 * Desktop: pinned left and vertically centred, always expanded, and it publishes its own width as
 * a camera LENS SHIFT so the world stays visually centred in the space it leaves free.
 * Mobile: bottom of the screen, collapsed to the wordmark + chevron until pressed.
 *
 * Rows are pure composition over `ui/kit` — the panel decides WHAT the controls are, the kit
 * decides how they look. Anything visual belongs in the kit, not here.
 *
 * Deliberately NOT here: FoV, MSAA, and the two distance axes as separate controls (Distance
 * pairs them). Those live in the debug panel; this card is the short list a player reaches for.
 */

import { useEffect, useRef, useState } from 'react';
import { Plus, ChevronUp } from 'lucide-react';
import { useGameStore } from '../state/store';
import type { WorldGenConfig } from '@worldify/shared';
import { materialManager } from '../game/material';
import {
  QUALITY_LABELS, QUALITY_LEVELS, DISTANCE_PRESETS, type QualityLevel,
} from '../game/quality/QualityPresets';
import { applyQualityPatch, syncQualityToStore } from '../game/quality/QualityManager';
import { formatTimeOfDay } from '../game/scene/DayNightCycle';
import { setExploreViewShiftX } from '../game/scene/ExploreCamera';
import {
  Panel, PanelStack, PanelRow, SegmentedToggle, PanelDropdown, PanelStepper, PanelSlider,
  MOTION, ACCENT_TEXT, type OptionItem,
} from './kit';
import { useIsDesktopLayout } from './useDeviceMode';
import { NewWorldDialog } from './NewWorldDialog';
import {
  listWorlds, getActiveWorld, setActiveWorld, createAndActivateWorld, deleteWorld,
  subscribeWorldsChanged, type WorldMeta,
} from '../game/world/WorldManager';

type TextureChoice = 'off' | 'low' | 'high';

const TEXTURE_OPTIONS: OptionItem<TextureChoice>[] = [
  { value: 'off', label: 'Off' },
  { value: 'low', label: 'Low' },
  { value: 'high', label: 'High' },
];

const QUALITY_OPTIONS: OptionItem<QualityLevel>[] =
  QUALITY_LEVELS.map((l) => ({ value: l, label: QUALITY_LABELS[l] }));

// Value is the index into DISTANCE_PRESETS — the pair (view distance, rings) has no single number
// to key on, and the index keeps the toggle's generic value type intact.
const DISTANCE_OPTIONS: OptionItem<number>[] =
  DISTANCE_PRESETS.map((p, i) => ({ value: i, label: p.label }));

const RESOLUTION_OPTIONS: OptionItem<number>[] = [25, 50, 100].map((n) => ({ value: n, label: `${n}%` }));

/** Mirrors the body's expand transition, after which the clip has to come off (see `clip` below). */
const EXPAND_MS = 220;

export function ExplorePanel() {
  const textureState = useGameStore((s) => s.textureState);
  const textureProgress = useGameStore((s) => s.textureProgress);
  const qualityLevel = useGameStore((s) => s.qualityLevel);
  const visibilityRadius = useGameStore((s) => s.quality.visibilityRadius);
  const farLodLevels = useGameStore((s) => s.quality.farLodLevels);
  const renderScale = useGameStore((s) => s.renderScale);
  const setRenderScale = useGameStore((s) => s.setRenderScale);
  const timeOfDay = useGameStore((s) => s.environment.timeOfDay);
  const setTimeOfDay = useGameStore((s) => s.setTimeOfDay);

  const desktop = useIsDesktopLayout();
  const cardRef = useRef<HTMLDivElement>(null);

  const [worlds, setWorlds] = useState<WorldMeta[]>([]);
  const [activeWorldId, setActiveWorldId] = useState<string | null>(null);
  const [showNewWorld, setShowNewWorld] = useState(false);
  const [openOnTouch, setOpenOnTouch] = useState(false);

  const expanded = desktop || openOnTouch;
  // The body is clipped only WHILE it animates: leaving overflow-hidden on would cut off the world
  // dropdown, which opens upward out of the card.
  const [clip, setClip] = useState(!expanded);
  useEffect(() => {
    if (!expanded) { setClip(true); return; }
    const t = setTimeout(() => setClip(false), EXPAND_MS);
    return () => clearTimeout(t);
  }, [expanded]);

  const refreshWorlds = () => {
    listWorlds().then((w) => {
      setWorlds(w.slice().sort((a, b) => b.lastPlayedAt - a.lastPlayedAt));
      setActiveWorldId(getActiveWorld()?.id ?? null);
    });
  };
  // Refresh on mount and whenever the world list / active world changes (e.g. once the
  // local world finishes booting after this panel first mounts).
  useEffect(() => {
    refreshWorlds();
    return subscribeWorldsChanged(refreshWorlds);
  }, []);

  // THE WORLD IS FRAMED AROUND THE CARD (desktop only). The free space is [card.right, W], whose
  // centre sits card.right/2 to the right of the screen centre — that is the shift. Measured rather
  // than derived from the width token so a restyle can't silently mis-frame the world.
  useEffect(() => {
    if (!desktop) { setExploreViewShiftX(0); return; }
    const el = cardRef.current;
    if (!el) return;
    const publish = () => setExploreViewShiftX(el.getBoundingClientRect().right / 2);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', publish);
      setExploreViewShiftX(0);
    };
  }, [desktop]);

  // THE TOGGLE SHOWS WHAT IS ON SCREEN, not what was asked for: 'off' means the flat pallet
  // placeholders are drawing, which is also the boot state. A load in flight leaves the pill on the
  // resolution currently installed and puts its progress on the segment it is loading TO.
  const loading = textureState === 'loading-low' ? 'low' : textureState === 'loading-high' ? 'high' : null;
  const textures: TextureChoice = textureState === 'high' ? 'high' : textureState === 'low' ? 'low' : 'off';
  const selectTextures = (choice: TextureChoice) => {
    if (choice === 'off') materialManager.disableTextures();
    else if (choice === 'low') void materialManager.downgradeToLowResolution();
    else void materialManager.upgradeToHighResolution();
  };

  // -1 when the live pair is off-list (the debug panel can set either axis freely) — the toggle
  // then shows no pill rather than claiming a preset that isn't in effect.
  const distanceIndex = DISTANCE_PRESETS.findIndex(
    (p) => p.view === visibilityRadius && p.far === farLodLevels,
  );

  const selectWorld = async (id: string) => { await setActiveWorld(id); refreshWorlds(); };
  const removeWorld = async (id: string) => { await deleteWorld(id); refreshWorlds(); };
  const createWorld = async (name: string, seed: number, genConfig: WorldGenConfig) => {
    setShowNewWorld(false);
    await createAndActivateWorld(name, seed, genConfig);
    refreshWorlds();
  };

  const logo = (
    <img src="/wrldy-logo-color.svg" alt="wrldy" className="h-10 md:h-11 w-auto shrink-0" />
  );

  return (
    <>
      <Panel className="pointer-events-auto w-[min(92vw,20rem)]" ref={cardRef}>
        {desktop ? (
          <div>{logo}</div>
        ) : (
          <button
            type="button"
            onClick={() => setOpenOnTouch((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse settings' : 'Expand settings'}
            className="w-full flex items-center justify-between gap-2 cursor-pointer"
          >
            {logo}
            <ChevronUp size={20} className={`${ACCENT_TEXT} ${MOTION} ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}

        <div
          className={`${clip ? 'overflow-hidden' : ''} ${MOTION} ${
            expanded ? 'max-h-[70dvh] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <PanelStack>
            <PanelRow label="World">
              <PanelDropdown
                value={activeWorldId}
                options={worlds.map((w) => ({ value: w.id, label: w.name }))}
                onSelect={selectWorld}
                onDelete={removeWorld}
                action={{ label: 'New World', icon: <Plus size={15} className="shrink-0" />, onClick: () => setShowNewWorld(true) }}
                placeholder="Worlds"
                ariaLabel="Active world"
              />
            </PanelRow>

            <PanelRow label="Textures">
              <SegmentedToggle
                options={TEXTURE_OPTIONS}
                value={textures}
                onSelect={selectTextures}
                busy={loading !== null}
                overrideLabel={loading ? { value: loading, label: `${Math.round(textureProgress * 100)}%` } : undefined}
                ariaLabel="Textures"
              />
            </PanelRow>

            <PanelRow label="Quality">
              <PanelStepper
                options={QUALITY_OPTIONS}
                value={qualityLevel}
                // Keep the live view settings across a preset switch — the preset's own values for
                // these two are only a fallback (see QualitySettings.visibilityRadius).
                onSelect={(l) => syncQualityToStore(l, visibilityRadius, farLodLevels)}
                ariaLabel="Quality preset"
              />
            </PanelRow>

            <PanelRow label="Distance">
              <PanelStepper
                options={DISTANCE_OPTIONS}
                value={distanceIndex}
                onSelect={(i) => applyQualityPatch({
                  visibilityRadius: DISTANCE_PRESETS[i].view,
                  farLodLevels: DISTANCE_PRESETS[i].far,
                })}
                ariaLabel="View distance"
              />
            </PanelRow>

            <PanelRow label="Resolution">
              <PanelStepper
                options={RESOLUTION_OPTIONS}
                value={Math.round(renderScale * 100)}
                onSelect={(n) => setRenderScale(n / 100)}
                ariaLabel="Render resolution"
              />
            </PanelRow>

            <PanelRow label="Time" value={formatTimeOfDay(timeOfDay)}>
              <PanelSlider min={0} max={1} step={0.005} value={timeOfDay} onChange={setTimeOfDay} ariaLabel="Time of day" />
            </PanelRow>
          </PanelStack>
        </div>
      </Panel>

      {showNewWorld && <NewWorldDialog onCancel={() => setShowNewWorld(false)} onCreate={createWorld} />}
    </>
  );
}
